import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  CalendarCheck,
  Loader2,
  Plug,
  Settings as SettingsIcon,
  Activity,
} from "lucide-react";
import { Link } from "react-router-dom";

interface HealthCheck {
  deployed: boolean;
  settings: { enabled: boolean; recipient: string | null };
  connector: {
    status: "verified" | "skipped" | "failed" | "unreachable";
    error: string | null;
    latencyMs: number | null;
  };
}

interface SyncLogRow {
  id: string;
  booking_id: string | null;
  action: string;
  recipient: string | null;
  status: string;
  error_message: string | null;
  created_at: string;
}

const statusBadge = (ok: boolean | "warn", label: string) => {
  if (ok === true)
    return (
      <Badge className="bg-green-500/15 text-green-700 border-green-500/30 hover:bg-green-500/20">
        <CheckCircle2 className="mr-1 h-3 w-3" /> {label}
      </Badge>
    );
  if (ok === "warn")
    return (
      <Badge className="bg-amber-500/15 text-amber-700 border-amber-500/30 hover:bg-amber-500/20">
        <AlertCircle className="mr-1 h-3 w-3" /> {label}
      </Badge>
    );
  return (
    <Badge className="bg-destructive/15 text-destructive border-destructive/30 hover:bg-destructive/20">
      <XCircle className="mr-1 h-3 w-3" /> {label}
    </Badge>
  );
};

const logStatusBadge = (status: string) => {
  if (status === "sent")
    return <Badge className="bg-green-500/15 text-green-700 border-green-500/30">Sent</Badge>;
  if (status.startsWith("skipped"))
    return <Badge variant="outline" className="text-muted-foreground">{status.replace("skipped_", "Skipped: ")}</Badge>;
  return <Badge variant="destructive">{status}</Badge>;
};

const CalendarSyncHealth = () => {
  const { toast } = useToast();
  const [checking, setChecking] = useState(false);
  const [health, setHealth] = useState<HealthCheck | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [logs, setLogs] = useState<SyncLogRow[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);

  const runHealthCheck = async () => {
    setChecking(true);
    setHealthError(null);
    try {
      const { data, error } = await supabase.functions.invoke("sync-outlook-calendar", {
        body: { dryRun: true },
      });
      if (error) throw error;
      setHealth(data as HealthCheck);
      setLastCheckedAt(new Date());
    } catch (err: any) {
      console.error("Health check failed:", err);
      setHealth(null);
      setHealthError(err?.message || "Failed to reach edge function — it may not be deployed.");
      toast({
        title: "Health check failed",
        description: err?.message || "Edge function unreachable",
        variant: "destructive",
      });
    } finally {
      setChecking(false);
    }
  };

  const loadLogs = async () => {
    setLoadingLogs(true);
    try {
      const { data, error } = await supabase
        .from("outlook_sync_log")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(25);
      if (error) throw error;
      setLogs((data || []) as SyncLogRow[]);
    } catch (err) {
      console.error("Load logs failed:", err);
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    runHealthCheck();
    loadLogs();
  }, []);

  // Derived status
  const deploymentOk = !!health && !healthError;
  const settingsOk = !!health?.settings.enabled && !!health?.settings.recipient;
  const connectorOk =
    !!health && (health.connector.status === "verified" || health.connector.status === "skipped");

  const recentSent = logs.filter((l) => l.status === "sent");
  const recentFailed = logs.filter((l) => l.status === "failed");
  const hasRecentSuccess = recentSent.length > 0;

  const overallOk = deploymentOk && settingsOk && connectorOk;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-light tracking-wide flex items-center gap-2">
            <CalendarCheck className="h-6 w-6 text-primary" />
            Calendar Sync Health
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Verify the Outlook calendar sync is deployed, enabled, and delivering invites.
          </p>
        </div>
        <Button onClick={() => { runHealthCheck(); loadLogs(); }} disabled={checking}>
          {checking ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking…</>
          ) : (
            <><RefreshCw className="mr-2 h-4 w-4" /> Re-run check</>
          )}
        </Button>
      </div>

      {/* Overall banner */}
      <Card
        className={`border-2 ${
          overallOk && hasRecentSuccess
            ? "border-green-500/40 bg-green-500/5"
            : overallOk
            ? "border-amber-500/40 bg-amber-500/5"
            : "border-destructive/40 bg-destructive/5"
        }`}
      >
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            {overallOk && hasRecentSuccess ? (
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            ) : overallOk ? (
              <AlertCircle className="h-8 w-8 text-amber-600" />
            ) : (
              <XCircle className="h-8 w-8 text-destructive" />
            )}
            <div>
              <p className="font-medium text-lg">
                {overallOk && hasRecentSuccess
                  ? "Calendar sync is healthy"
                  : overallOk
                  ? "Configured, but no recent successful sends"
                  : "Calendar sync needs attention"}
              </p>
              <p className="text-sm text-muted-foreground">
                {lastCheckedAt
                  ? `Last checked ${lastCheckedAt.toLocaleTimeString()}`
                  : "Running first check…"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Individual checks */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Deployment */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-light tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
              <Activity size={16} /> Edge Function Deployment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm">sync-outlook-calendar</span>
              {checking
                ? <Badge variant="outline"><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Checking</Badge>
                : statusBadge(deploymentOk, deploymentOk ? "Deployed" : "Not reachable")}
            </div>
            {healthError && (
              <p className="text-xs text-destructive break-words">{healthError}</p>
            )}
          </CardContent>
        </Card>

        {/* Settings */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-light tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
              <SettingsIcon size={16} /> Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm">Sync enabled</span>
              {statusBadge(!!health?.settings.enabled, health?.settings.enabled ? "On" : "Off")}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Recipient</span>
              {health?.settings.recipient ? (
                <span className="text-sm font-mono text-foreground">{health.settings.recipient}</span>
              ) : (
                statusBadge(false, "Not set")
              )}
            </div>
            <Link
              to="/admin/settings"
              className="text-xs text-primary hover:underline inline-block pt-1"
            >
              Edit settings →
            </Link>
          </CardContent>
        </Card>

        {/* Connector */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-light tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
              <Plug size={16} /> Outlook Connector
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm">Credentials</span>
              {checking
                ? <Badge variant="outline"><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Checking</Badge>
                : statusBadge(connectorOk, health?.connector.status || "unknown")}
            </div>
            {health?.connector.latencyMs != null && (
              <div className="flex items-center justify-between">
                <span className="text-sm">Gateway latency</span>
                <span className="text-sm text-muted-foreground">{health.connector.latencyMs} ms</span>
              </div>
            )}
            {health?.connector.error && (
              <p className="text-xs text-destructive break-words">{health.connector.error}</p>
            )}
          </CardContent>
        </Card>

        {/* Recent activity summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-light tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
              <Activity size={16} /> Recent Activity (last 25)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm">Successful sends</span>
              {statusBadge(recentSent.length > 0 ? true : "warn",
                `${recentSent.length}`)}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Failures</span>
              {statusBadge(recentFailed.length === 0,
                `${recentFailed.length}`)}
            </div>
            {!hasRecentSuccess && (
              <p className="text-xs text-muted-foreground">
                No successful invocations yet. Create or re-confirm a booking to trigger a sync.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent log table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.2em] uppercase text-muted-foreground">
            Recent Sync Attempts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingLogs ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : logs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No sync attempts logged yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {new Date(row.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="capitalize text-sm">{row.action}</TableCell>
                      <TableCell>{logStatusBadge(row.status)}</TableCell>
                      <TableCell className="text-xs font-mono">{row.recipient || "—"}</TableCell>
                      <TableCell className="text-xs text-destructive max-w-[280px] truncate">
                        {row.error_message || "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarSyncHealth;
